// --------------------------------------------------------------
// Created On: 2025-06-24
// Author: Dimitra Weinstein
//
// Last Modified: 2025-12-19
// Modified By: Marco Serrano
//
// Copyright 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { forwardRef, useImperativeHandle, useRef } from "react";
import styles from "./SalesQuoteDetails.module.scss";
import {
  CORNELL_ADDRESS_LINE_ONE,
  CORNELL_ADDRESS_LINE_THREE,
  CORNELL_ADDRESS_LINE_TWO,
  CORNELL_WEB_SITE,
  SALES_CONTACT_EMAIL_ADDRESS,
  SALES_CONTACT_FAX_NUMBER,
  SALES_CONTACT_PHONE_NUMBER,
} from "../../constants/miscellaneous";
import { useReactToPrint } from "react-to-print";

// Displays information on a completed sales quote.
function InnerSalesQuoteDetails(props: Props, ref: React.Ref<{ print: () => void }>): Component {
  const contentRef = useRef(null);

  const handlePrint = useReactToPrint({
    contentRef: contentRef,
    documentTitle: props.quoteName,
  });

  // Exposing a print event to the parent component so that the contents of this page can be printed
  useImperativeHandle(ref, () => ({
    print: () => {
      handlePrint();
    },
  }));

  // Paint color, impeller angle and impeller trim are stored as a comment for the line item. Create the string to be displayed
  function createImpellerConfigurationString(cartItem: CartItem) {
    let configurationString = "N/A";
    let angleString = "";
    let trimString = "";
    if (cartItem.partNumber.includes("-3-")) {
      if (cartItem.impellerAngle !== "" && cartItem.impellerAngle !== null) {
        angleString = `Impeller Angle: ${parseInt(cartItem.impellerAngle).toString()}°`;
      }
      if (cartItem.impellerTrim !== "" && cartItem.impellerTrim !== null) {
        trimString = `Impeller Trim: ${
          parseFloat(cartItem.impellerTrim) === 0 || cartItem.impellerTrim === "Full"
            ? "Full Trim"
            : `${parseFloat(cartItem.impellerTrim).toFixed(2).toString()} inches`
        }`;
      }
    }
    if (angleString !== "" || trimString !== "") {
      configurationString = angleString + " | " + trimString;
    }
    return configurationString;
  }

  // Get the selected paint color.
  function getPaintColor(paintColors: PaintColor[], selectedPaintColorId: number | null): string {
    const paintColorIndex = paintColors.findIndex((paintColor) => paintColor.paintColorId === selectedPaintColorId);
    if (paintColorIndex > -1) {
      return paintColors[paintColorIndex].name;
    }
    return "N/A";
  }

  return (
    // Sales Quote Mobile View
    <div className="card p-2" ref={contentRef}>
      <div className="mobile-view d-block d-lg-none">
        {/* Company Logo */}
        <div className="col text-center mb-2">
          <img className={`${styles.logo} m-2`} src="/logo.png" alt="Shop Cornell Pump" />
        </div>
        {/* Cornell Address Info */}
        <div className="col text-center">
          <ul style={{ padding: "0" }}>
            <li>{CORNELL_ADDRESS_LINE_ONE}</li>
            <li>{CORNELL_ADDRESS_LINE_TWO}</li>
            <li>{CORNELL_ADDRESS_LINE_THREE}</li>
            <li>{CORNELL_WEB_SITE}</li>
          </ul>
        </div>
        {/* Cornell Sales Contact Info */}
        <div className="col text-center">
          <ul style={{ padding: "0" }}>
            <li>
              <div className="col">
                Phone: <span className="font-weight-bold">{SALES_CONTACT_PHONE_NUMBER}</span>
              </div>
            </li>
            <li>
              <div className="col">
                Fax: <span className="font-weight-bold">{SALES_CONTACT_FAX_NUMBER}</span>
              </div>
            </li>
            <li>
              <div className="col">
                Email: <span className="font-weight-bold">{SALES_CONTACT_EMAIL_ADDRESS}</span>
              </div>
            </li>
          </ul>
        </div>
        {/* Small Screen, Mobile View */}
        <div className="d-none d-md-block">
          <div className=" card border-secondary m-1">
            <div className="p-2">
              <div className="row">
                <div className="col-3"></div>
                <div className="col-6 text-center">
                  <h4>Shipping Address</h4>
                  <ul style={{ padding: "0" }}>
                    {props.shippingCompanyName !== "" && (
                      <li className={styles.content}>{props.shippingCompanyName}</li>
                    )}
                    {props.shippingContactName !== "" && (
                      <li className={styles.content}>{props.shippingContactName}</li>
                    )}
                    <li className={styles.content}>{props.shippingAddressLine1}</li>
                    {props.shippingAddressLine2 !== "" && (
                      <li className={styles.content}>{props.shippingAddressLine2}</li>
                    )}
                    <li className={styles.content}>
                      {props.shippingCity}, {props.shippingState} {props.shippingZipCode}
                    </li>
                    <li className={styles.content}>{props.shippingCountry}</li>
                    {props.shippingPhoneNumber !== "" && (
                      <li className={styles.content}>{props.shippingPhoneNumber}</li>
                    )}
                    {props.specialInstructions !== "" && (
                      <li className={styles.content}>Special Instructions: {props.specialInstructions}</li>
                    )}
                  </ul>
                </div>
                <div className="col-3"></div>
              </div>
            </div>
          </div>
          <div className={"card border-secondary m-1"}>
            <div className="p-2">
              <div className="row">
                <div className="col-3"></div>
                <div className="col-6 text-center">
                  <h4>Quote Summary</h4>
                  <ul style={{ padding: "0" }}>
                    <li className={styles.content}>
                      <div className="col ">
                        Order Number:{" "}
                        <span className="font-weight-bold">
                          {props.orderNumber === null ? "N/A" : props.orderNumber.toString()}
                        </span>
                      </div>
                    </li>
                    <li className={styles.content}>
                      <div className="col ">
                        PO Number: <span className="font-weight-bold">{props.poNumber}</span>
                      </div>
                    </li>
                    <li className={styles.content}>
                      <div className="col ">
                        Item(s) Subtotal: $
                        <span className="font-weight-bold">
                          {props.subTotal
                            ? parseFloat(String(props.subTotal)).toLocaleString("en-US", { minimumFractionDigits: 2 })
                            : "N/A"}
                        </span>
                      </div>
                    </li>
                    <li className={styles.content}>
                      <div className="col ">
                        Company Discount: <span className="font-weight-bold">{props.companyDiscount}%</span>
                      </div>
                    </li>
                    <li className={styles.content}>
                      <div className="col ">
                        Grand Total:{" "}
                        <span className="font-weight-bold">
                          ${parseFloat(String(props.grandTotal)).toLocaleString("en-US", { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                    </li>
                  </ul>
                </div>
                <div className="col-3"></div>
              </div>
            </div>
          </div>
        </div>
        {/* Mid-size screen, Mobile View */}
        <div className="d-block d-md-none">
          <div className="card border-secondary m-1">
            <div className="p-2">
              <div className="text-center">
                <h4>Shipping Address</h4>
                <ul style={{ padding: "0" }}>
                  {props.shippingCompanyName !== "" && <li className={styles.content}>{props.shippingCompanyName}</li>}
                  {props.shippingContactName !== "" && <li className={styles.content}>{props.shippingContactName}</li>}
                  <li className={styles.content}>{props.shippingAddressLine1}</li>
                  {props.shippingAddressLine2 !== "" && (
                    <li className={styles.content}>{props.shippingAddressLine2}</li>
                  )}
                  <li className={styles.content}>
                    {props.shippingCity}, {props.shippingState} {props.shippingZipCode}
                  </li>
                  <li className={styles.content}>{props.shippingCountry}</li>
                  {props.shippingPhoneNumber !== "" && <li className={styles.content}>{props.shippingPhoneNumber}</li>}
                  {props.specialInstructions !== "" && (
                    <li className={styles.content}>Special Instructions: {props.specialInstructions}</li>
                  )}
                </ul>
              </div>
            </div>
          </div>
          <div className="card border-secondary m-1">
            <div className="p-2">
              <div className="text-center">
                <h4>Quote Summary</h4>
                <ul style={{ padding: "0" }}>
                  <li className={styles.content}>
                    <div className="col ">
                      Order Number:{" "}
                      <span className="font-weight-bold">
                        {props.orderNumber === null ? "N/A" : props.orderNumber.toString()}
                      </span>
                    </div>
                  </li>
                  <li className={styles.content}>
                    <div className="col">
                      PO Number: <span className="font-weight-bold">{props.poNumber}</span>
                    </div>
                  </li>
                  <li className={styles.content}>
                    <div className="col">
                      Item(s) Subtotal: $
                      <span className="font-weight-bold">
                        {props.subTotal
                          ? parseFloat(String(props.subTotal)).toLocaleString("en-US", { minimumFractionDigits: 2 })
                          : "N/A"}
                      </span>
                    </div>
                  </li>
                  <li className={styles.content}>
                    <div className="col">
                      Company Discount: <span className="font-weight-bold">{props.companyDiscount}%</span>
                    </div>
                  </li>
                  <li className={styles.content}>
                    <div className="col">
                      Grand Total:{" "}
                      <span className="font-weight-bold">
                        ${parseFloat(String(props.grandTotal)).toLocaleString("en-US", { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        <div className="pt-3">
          <div className={`${styles.orderItems} card border-secondary`}>
            <div className="table-responsive">
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th className={styles.content} scope="col">
                      Qty.
                    </th>
                    <th className={styles.content} scope="col">
                      Part #
                    </th>
                    <th className={styles.content} scope="col">
                      Desc.
                    </th>
                    <th className={styles.content} scope="col">
                      Paint Color
                    </th>

                    <th className={styles.content} scope="col">
                      Price
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {props.cart.map((cartItem) => (
                    <tr
                      key={`${cartItem.partNumber}-${cartItem.paintColorId}-${createImpellerConfigurationString(
                        cartItem
                      )}`}
                    >
                      <td className={styles.content}>{cartItem.quantity}</td>
                      <td className={styles.content}>{cartItem.partNumber}</td>
                      <td className={styles.content}>{cartItem.description}</td>
                      <td className={styles.content}>{getPaintColor(props.paintColors, cartItem.paintColorId)}</td>
                      <td className={styles.content}>${cartItem.price}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Sales Quote Small Screen Mobile View */}
      <div className="non-mobile-view d-none d-lg-block">
        <div className={`${styles.printContent}`}>
          <div className="row p-2 justify-content-between">
            <div className="col">
              <ul style={{ padding: "0" }}>
                <li data-test="cornell-address-line-one">{CORNELL_ADDRESS_LINE_ONE}</li>
                <li data-test="cornell-address-line-two">{CORNELL_ADDRESS_LINE_TWO}</li>
                <li data-test="cornell-address-line-three">{CORNELL_ADDRESS_LINE_THREE}</li>
                <li data-test="cornell-website">{CORNELL_WEB_SITE}</li>
              </ul>
            </div>
            <div className="col text-center mb-2" data-test="cornell-logo">
              <img className={`${styles.logo} m-0`} src="/logo.png" alt="Shop Cornell Pump" />
            </div>
            <div className="col">
              <ul style={{ padding: "0" }}>
                <li>
                  <div className="row">
                    <div className="col text-start">Phone:</div>
                    <div className="col text-end font-weight-bold" data-test="sales-contact-phone">
                      {SALES_CONTACT_PHONE_NUMBER}
                    </div>
                  </div>
                </li>
                <li>
                  <div className="row">
                    <div className="col text-start">Fax:</div>
                    <div className="col text-end font-weight-bold" data-test="sales-contact-fax">
                      {SALES_CONTACT_FAX_NUMBER}
                    </div>
                  </div>
                </li>
                <li>
                  <div className="row">
                    <div className="col text-start">Email:</div>
                    <div className="col text-end font-weight-bold" data-test="sales-contact-email">
                      {SALES_CONTACT_EMAIL_ADDRESS}
                    </div>
                  </div>
                </li>
              </ul>
            </div>
          </div>
          <div className="card border-secondary">
            <div className="p-3">
              <div className="row justify-content-between">
                <div className="col col-lg-5">
                  <h4>Shipping Address</h4>
                  <ul style={{ padding: "0" }}>
                    {props.shippingCompanyName !== "" && (
                      <li className={styles.content} data-test="shipping-company-name">
                        {props.shippingCompanyName}
                      </li>
                    )}
                    {props.shippingContactName !== "" && (
                      <li className={styles.content} data-test="shipping-contact-name">
                        {props.shippingContactName}
                      </li>
                    )}
                    <li className={styles.content} data-test="shipping-address-line-one">
                      {props.shippingAddressLine1}
                    </li>
                    {props.shippingAddressLine2 !== "" && (
                      <li className={styles.content} data-test="shipping-address-line-two">
                        {props.shippingAddressLine2}
                      </li>
                    )}
                    {props.shippingCity !== "" && props.shippingState !== "" && props.shippingZipCode !== 0 && (
                      <li className={styles.content} data-test="shipping-city-state-zip">
                        {props.shippingCity}, {props.shippingState} {props.shippingZipCode}
                      </li>
                    )}
                    <li className={styles.content} data-test="shipping-country">
                      {props.shippingCountry}
                    </li>
                    {props.shippingPhoneNumber !== "" && (
                      <li className={styles.content} data-test="shipping-phone-number">
                        {props.shippingPhoneNumber}
                      </li>
                    )}
                    {props.specialInstructions !== "" && (
                      <li className={styles.content} data-test="special-instructions">
                        Special Instructions: {props.specialInstructions}
                      </li>
                    )}
                  </ul>
                </div>
                <div className="col col-lg-5">
                  <h4>Quote Summary</h4>
                  <ul style={{ padding: "0" }}>
                    <li className={styles.content}>
                      <div className="row">
                        <div className="col text-start">Order Number:</div>
                        <div className="col text-end font-weight-bold" data-test="summary-order-number">
                          {props.orderNumber}
                        </div>
                      </div>
                    </li>
                    <li className={styles.content}>
                      <div className="row">
                        <div className="col text-start">PO Number:</div>
                        <div className="col text-end font-weight-bold" data-test="summary-po-number">
                          {props.poNumber}
                        </div>
                      </div>
                    </li>
                    <li className={styles.content}>
                      <div className="row">
                        <div className="col text-start">Item(s) Subtotal:</div>
                        <div className="col text-end font-weight-bold" data-test="summary-subtotal">
                          $
                          {props.subTotal
                            ? parseFloat(String(props.subTotal)).toLocaleString("en-US", { minimumFractionDigits: 2 })
                            : "N/A"}
                        </div>
                      </div>
                    </li>
                    <li className={styles.content}>
                      <div className="row">
                        <div className="col-9 text-start" data-test="summary-discount-distributor-name">
                          {props.distributorName} Discount:
                        </div>
                        <div className="col text-end font-weight-bold" data-test="summary-discount-amount">
                          {props.companyDiscount}%
                        </div>
                      </div>
                    </li>
                    <li className={styles.content}>
                      <div className="row">
                        <div className="col-7 text-start">Grand Total:</div>
                        <div className="col text-end font-weight-bold" data-test="summary-grand-total">
                          $
                          {props.grandTotal
                            ? parseFloat(String(props.grandTotal)).toLocaleString("en-US", { minimumFractionDigits: 2 })
                            : "N/A"}
                        </div>
                      </div>
                    </li>
                    {props.shippingBillingType !== null &&
                      props.shippingBillingType !== undefined &&
                      props.shippingBillingType !== "" && (
                        <li className={styles.content}>
                          <div className="row">
                            <div className="col-7 text-start">Billing Type:</div>
                            <div className="col text-end font-weight-bold">{props.shippingBillingType}</div>
                          </div>
                        </li>
                      )}
                  </ul>
                </div>
              </div>
            </div>
          </div>
          <div className="pt-3">
            <div className={`${styles.orderItems} card border-secondary`}>
              <div className="table-responsive">
                <table className="table table-sm" data-test="quote-items-table">
                  <thead>
                    <tr>
                      <th className={styles.content} scope="col">
                        Qty.
                      </th>
                      <th className={styles.content} scope="col">
                        Part Number
                      </th>
                      <th className={styles.content} scope="col">
                        Description
                      </th>
                      <th className={styles.content} scope="col">
                        Paint Color
                      </th>
                      <th className={styles.content} scope="col">
                        Impeller Configuration
                      </th>
                      <th className={styles.content} scope="col">
                        Unit Price
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {props.cart.map((cartItem) => (
                      <tr
                        key={`${cartItem.partNumber}-${cartItem.paintColorId}-${createImpellerConfigurationString(
                          cartItem
                        )}`}
                        data-test="quote-item-row"
                      >
                        <td className={styles.content} data-test="quote-item-quantity">
                          {cartItem.quantity}
                        </td>
                        <td className={styles.content} data-test="quote-item-part-number">
                          {cartItem.partNumber}
                        </td>
                        <td className={styles.content} data-test="quote-item-description">
                          {cartItem.description}
                        </td>
                        <td className={styles.content} data-test="quote-item-paint-color">
                          {getPaintColor(props.paintColors, cartItem.paintColorId)}
                        </td>
                        <td className={styles.content} data-test="quote-item-impeller-configuration">
                          {createImpellerConfigurationString(cartItem)}
                        </td>
                        <td className={styles.content} data-test="quote-item-price">
                          ${cartItem.price}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Wrapping InnerSalesQuote so that we can forward the ref from SalesQuoteDetails -> div with print event
const SalesQuoteDetails = forwardRef<{ print: () => void }, Props>(InnerSalesQuoteDetails);

interface Props {
  paintColors: PaintColor[];
  loading: boolean;
  salesQuoteId: number;
  allowPartialShipments: boolean | null;
  cart: CartItem[];
  comment: string;
  companyDiscount: number | null;
  distributorName: string | null;
  grandTotal: number;
  subTotal: number;
  poNumber: string;
  orderNumber: number | null;
  quoteName: string;
  shippingAddressLine1: string;
  shippingAddressLine2: string | null;
  shippingCity: string;
  shippingCompanyName: string;
  shippingContactName: string;
  shippingCountry: string;
  shippingPhoneNumber: string;
  shippingState: string;
  shippingZipCode: number;
  shippingBillingType: string | null;
  shippingBillingAccountNumber: string | null;
  specialInstructions: string | null;
  onChangeLoading: (loading: boolean) => void;
  onChangeFailedToLoad: (failedToLoad: boolean) => void;
  onChangeErrorMessage: (errorMessage: string) => void;
}

interface CartItem {
  partId: number;
  partNumber: string;
  quantity: number;
  paintColorId: number | null;
  impellerTrim: string | null;
  impellerAngle: string | null;
  price: number;
  description: string;
}

interface PaintColor {
  paintColorId: number;
  hexCode: string;
  name: string;
}

export default SalesQuoteDetails;
